import {
  faCreditCard,
  faFileInvoice,
  faFileInvoiceDollar,
  faFolderOpen,
  faTicketAlt,
  faWallet,
} from '@fortawesome/pro-light-svg-icons';
import {
  billingFlagTransformer,
  useBilling,
  useBillingErrors,
  useBillingStore,
} from '@teemill/modules/billing';
import { useMenu, type MenuItem } from '@teemill/modules/menu';
import { useNotifications } from '@teemill/modules/notifications';
import { watchDeep } from '@vueuse/core';
import { watch } from 'vue';
import { useProject } from './projects';

export const menuItems: MenuItem[] = [
  {
    name: 'billing',
    title: 'Billing',
    to: { name: 'billing.index' },
    icon: faFileInvoice,
    order: 1000,
  },
  {
    name: 'billing.invoices',
    title: 'Invoices',
    to: { name: 'billing.invoices' },
    icon: faFileInvoice,
    order: 1001,
  },
  {
    name: 'billing.credit-notes',
    title: 'Credit Notes',
    to: { name: 'billing.credit-notes' },
    icon: faFileInvoiceDollar,
    order: 1002,
  },
  {
    name: 'billing.summaries',
    title: 'Summaries',
    to: { name: 'billing.summaries' },
    icon: faFolderOpen,
    order: 1003,
  },
  {
    name: 'billing.charges',
    title: 'Charges',
    to: { name: 'billing.charges' },
    icon: faTicketAlt,
    order: 1004,
  },
  {
    name: 'billing.payment-methods',
    title: 'Payment',
    to: { name: 'billing.payment-methods' },
    icon: faCreditCard,
    order: 1005,
  },
  {
    name: 'billing.wallet',
    title: 'Wallet',
    to: { name: 'billing.wallet' },
    icon: faWallet,
    order: 1006,
  },
];

export const refreshBillingProject = () => {
  const project = useProject({ required: true });
  const store = useBillingStore();
  const billing = useBilling(project.value.id);
  const errors = useBillingErrors();

  return billing.project
    .get()
    .then(({ data }) => {
      store.billing = data;
    })
    .catch(errors.collect);
};

export const setup = () => {
  const project = useProject();
  const store = useBillingStore();
  useMenu().addWhen(() => !!project.value && !store.hideBilling, ...menuItems);

  watch(
    () => project.value,
    async (project) => {
      store.billing = undefined;

      useNotifications().clear('announcement');

      if (!project) {
        return;
      }

      refreshBillingProject().finally(() => {
        store.ready = true;
      });
    },
    { immediate: true },
  );

  watchDeep(
    () => store.billing,
    (project) => {
      if (!project) {
        return;
      }

      useNotifications().clear('announcement');

      const { flags: flagsRaw, plan } = project;

      if (store.hideBilling) {
        return;
      }

      const flags = flagsRaw ?? [];
      const transformer = billingFlagTransformer();

      if (flags.includes('account_restricted')) {
        const error = transformer('account_restricted');
        if (error) {
          useNotifications().announcement(error.message, {
            id: 'account-restricted',
            level: error.level,
            actions: error.actions,
          });
        }

        return;
      }

      if (flags.includes('credit_control')) {
        const error = transformer('credit_control');
        if (error) {
          useNotifications().announcement(error.message, {
            id: 'credit-control-billing',
            level: error.level,
            actions: error.actions,
          });
        }

        return;
      }

      if (plan?.code === 'demo') {
        useNotifications().announcement('Project is in demo mode.', {
          level: 'default',
        });

        return;
      }

      flags.forEach((flag: string) => {
        const error = transformer(flag);
        if (error) {
          useNotifications().announcement(error.message, {
            level: error.level,
            actions: error.actions,
          });
        }
      });
    },
  );
};
